import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { DesignVersionProvider } from "@/design-version/provider";
import { syncDesign } from "@/design-version/store";
import { createRoot } from "react-dom/client";
import { AppErrorPage, NotFoundPage } from "@/components/app/brand-fallback";
import { RoutePending } from "@/components/app/route-pending";
import { installPopupScroll } from "@/lib/popup-scroll";
import { queryClient } from "@/lib/query-client";
import { routeTree } from "./routeTree.gen";
import "@/styles/globals.css";

const designReady = syncDesign(window.location.search);

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Never the router's own "Something went wrong!" panel: every route falls back to nexseed's screens.
  defaultErrorComponent: AppErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  // The router's own defaults are 1000/500, which on this app means a transition is over before
  // anything is ever drawn — so a slow one showed a blank screen instead. 200ms is past the point
  // where a wait reads as a stall; the 300ms floor stops it strobing on a fast one.
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 150,
  defaultPendingMinMs: 400,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

router.history.subscribe(({ location }) => {
  void syncDesign(location.search);
});

installPopupScroll();

void designReady.then(() =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <DesignVersionProvider>
          <RouterProvider router={router} />
        </DesignVersionProvider>
      </QueryClientProvider>
    </StrictMode>
  )
);
